import { INDUSTRY_OPTIONS } from '@/mock/industryType';
import { RELATIONSHIP_LEAD_OPTIONS_API } from '@/mock/relationshipLead';
import { RELATIONSHIP_STRENGH_OPTIONS_API } from '@/mock/relationshipStrengh';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { OptionItem } from './types';
import { RegionType } from '@/components/shared/profile-form/utils/types';

const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

const getInitials = (firstName: string, lastName: string) => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
  return firstInitial + lastInitial;
};

const getEnumKeyFromDisplayValue = (
  displayValue: string,
  options: [string, string, string?][],
): string | null => {
  const option = options.find(([, label]) => label === displayValue);
  return option ? option[0] : null;
};

const getEnumKeyFromId = (id: string, options: [string, string][]): string | null => {
  const option = options.find(([optionId]) => optionId === id);
  return option ? option[0] : null;
};

const getOptionFromId = (id: string, options: OptionItem[]): string | null => {
  const option = options.find((opt) => opt.value === id);
  return option ? option.value : null;
};

const getEnumKeyFromLabel = (label: string, options: OptionItem[]): string | null => {
  const option = options.find((opt) => opt.label === label);
  if (!option && label) {
    return RegionType.other;
  }
  return option ? option.value : null;
};

const getEnumKeysFromLabels = (labels: string[], options: OptionItem[]): string[] => {
  return labels.map((label) => {
    const option = options.find((opt) => opt.label === label);
    return option ? option.value : label;
  });
};

const getEnumLabelFromValue = (
  inputValue: string,
  options: [string, string, string?][],
): string | null => {
  const option = options.find(([value]) => value === inputValue);
  return option ? option[1] : null;
};

const getProfileDisplayEntry = (value: string | string[], options: OptionItem[]): string => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        const option = options.find((opt) => opt.value === item);
        return option ? option.label : item;
      })
      .join(', ');
  } else {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  }
};

const findRelationshipLeadElement = (id: string) =>
  RELATIONSHIP_LEAD_OPTIONS_API.find((option) => option.id.toString() === id);

const findRelationshipStrenghElement = (id: string) =>
  RELATIONSHIP_STRENGH_OPTIONS_API.find((option) => option.dropdownOptionId.toString() === id);

const findReltionshipStrengthElementByRank = (rank: number) =>
  RELATIONSHIP_STRENGH_OPTIONS_API.find((option) => option.rank === rank);

const findIndustryLabel = (value: string) =>
  INDUSTRY_OPTIONS.find((option) => option.value === value)?.label;

const isEmptyObject = (obj: object) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

function formatDateToLongString(dateString: string): string {
  const date = new Date(dateString);

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);

  return formattedDate;
}

const getRelationshipStrengthInferedValue = (scalar: number) => {
  const strengthLevel = scalar >= 0.75 ? 3 : scalar >= 0.25 ? 2 : 1;
  const option = findReltionshipStrengthElementByRank(strengthLevel);
  return option?.dropdownOptionId.toString() || '';
};

export {
  cn,
  getInitials,
  getEnumKeyFromLabel,
  getEnumKeyFromDisplayValue,
  getEnumKeysFromLabels,
  getEnumLabelFromValue,
  getProfileDisplayEntry,
  findRelationshipLeadElement,
  findRelationshipStrenghElement,
  findIndustryLabel,
  isEmptyObject,
  formatDateToLongString,
  getEnumKeyFromId,
  getRelationshipStrengthInferedValue,
  getOptionFromId,
};
